import { Modal, Typography, Select, Tooltip } from "antd";
import { audioMessage as message } from "../Common/Notifications/audioMessage";
import React from "react";
import { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "./styles.css";
import "@styles/styles-MetricsSettings.css";
import { TrainingAPI } from "../../api/TrainingAPI";
import { PiKeyFill, PiKeyBold, PiKey } from "react-icons/pi";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { MdDragIndicator } from "react-icons/md";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { getColorStrategies } from "../../store/actions/team.actions";
import {
  PermissionedButton,
  PermissionedInputNumber,
  PermissionedSelect,
  useUserPermissions
} from "../PermissionedComponents";

// Baselines in the baselines JSON keep up to 7 decimal places. UDMs and recent native
// metrics need this precision; the value applies to every table and report for the metric.
const METRIC_DECIMAL_MAX = 7;

// Baselines for these keys live in Players columns, not in the baselines JSON. Each column
// has its own scale, so it drops any digit past the cap below.
const BASELINE_COLUMN_DECIMAL_MAX = {
  top_speed: 2,
  trimp: 1,
  trimp_per_minute: 1,
  max_hr: 1,
};

const METRIC_SETTINGS_HELP = [
  ["Order", "Drag a metric to change where it appears on the platform."],
  ["Key metrics", "Select exactly 4. Key metrics drive intensity scores, GPS reports, and over/underworked flags."],
  ["Decimals", "The number of decimal places for this metric on the platform and in reports."],
  ["Weekly multiplier", "Key metrics only. The Overview weekly buckets use baseline × this value as the weekly benchmark."],
  ["Color strategy", "Sets the colors for this metric's values. Manage strategies in Custom Coloring."],
  ["Visibility", "Hidden metrics do not show on the platform. Key metrics are always visible."],
];

const SortableMetricItem = ({
  item,
  index,
  keyMetricSelection,
  showKeyIcon,
  hasEditMetricsPermission,
  custom_colors_enabled,
  colorStrategies,
  onKeyMetricClick,
  onVisibleClick,
  onDecimalChange,
  onWeeklyMultiplierChange,
  onColorStrategyChange,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `${item.key}`,
    disabled: !hasEditMetricsPermission,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: !hasEditMetricsPermission ? 'default' : isDragging ? 'grabbing' : 'grab',
  };

  const decimalMax = BASELINE_COLUMN_DECIMAL_MAX[item.key] ?? METRIC_DECIMAL_MAX;
  const isSelectedKey = keyMetricSelection?.includes(item.key);
  const canBeKey = item.is_key_metric || item.ACWR;
  const keyCursor = hasEditMetricsPermission ? "pointer" : "not-allowed";

  return (
    <div
      className="metric-card"
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      <div className="metric-card-name">
        <span className="metric-card-grip"><MdDragIndicator size={18} /></span>
        {canBeKey ? (
          <span
            className={`metric-card-key${isSelectedKey ? " selected" : ""}${hasEditMetricsPermission ? "" : " disabled"}`}
            style={{cursor: keyCursor}}
            onClick={() => onKeyMetricClick(item.key, index, !showKeyIcon[index])}
          >
            {isSelectedKey ? <PiKeyFill /> : <PiKeyBold />}
          </span>
        ) : (
          <Tooltip title="Only cumulative metrics can be key metrics.">
            <span className="metric-card-key disabled" style={{cursor: "not-allowed"}}><PiKey /></span>
          </Tooltip>
        )}
        {item.name}
        {item.is_user_defined && (
          <Tooltip title="Edit this metric in the User Defined Metrics settings page.">
            <InfoCircleOutlined style={{color: '#888888', fontWeight: 'normal'}} />
          </Tooltip>
        )}
      </div>

      <div className="metric-card-field">
        {!item.is_user_defined && (
          <>
            Decimals
            <Tooltip
              title={
                decimalMax < METRIC_DECIMAL_MAX
                  ? `This metric stores a baseline with at most ${decimalMax} decimal place(s).`
                  : ''
              }
            >
              <PermissionedInputNumber
                className="metric-card-decimals"
                min={0}
                max={decimalMax}
                value={item.decimal_places}
                size="small"
                permissionCheck="EditMetrics"
                onChange={(value) => onDecimalChange(item.key, value)}
              />
            </Tooltip>
          </>
        )}
      </div>

      <div className="metric-card-field">
        {isSelectedKey && (
          <>
            Weekly mult.
            <PermissionedInputNumber
              className="metric-card-number"
              value={item.weekly_bucket_multiple}
              size="small"
              permissionCheck="EditMetrics"
              onChange={(value) => onWeeklyMultiplierChange(item.key, value)}
            />
          </>
        )}
      </div>

      {custom_colors_enabled && (
        <div className="metric-card-strategy">
          {!item.is_user_defined && (
            <PermissionedSelect
              className="metric-card-select"
              placeholder="Select color strategy"
              value={item.color_strategy_id}
              size="small"
              popupMatchSelectWidth={false}
              permissionCheck="EditMetrics"
              onChange={(value) => onColorStrategyChange(item.key, value)}
            >
              {colorStrategies.map((strategy) => (
                <Select.Option key={strategy.id} value={strategy.id}>{strategy.name}</Select.Option>
              ))}
            </PermissionedSelect>
          )}
        </div>
      )}

      <div className="metric-card-eye" onClick={() => onVisibleClick(item, item.visible)}>
        <span style={{cursor: `${item.is_key_metric ? 'no-drop': hasEditMetricsPermission ? 'pointer' : 'not-allowed'}`}}>
          {item.visible ?
            <FaEye style={{opacity: `${item.is_key_metric ? '50%': '100%'}`}} /> : <FaEyeSlash />}
        </span>
      </div>
    </div>
  );
};
const trainingAPI = new TrainingAPI();
const { Text } = Typography;

export const MetricsSettings = ({ getAllMetrics}) => {
  const [metrics, setMetrics] = useState([])
  const [showKeyIcon, setShowKeyIcon] = useState({}); // State to control icon visibility
  const [keyMetricSelection, setKeyMetricSelection] = useState([]);
  const [itemList, setItemList] = useState(metrics);
  const [isModalVisible, setIsModalVisible] = useState(false);
  
  const dispatch = useDispatch();
  
  const {
    info: { custom_colors_enabled },
    color_strategies: colorStrategies
  } = useSelector(({ team }) => team);
  const userPermissions = useUserPermissions();

  // Check if user has permission to edit metrics
  const hasEditMetricsPermission = userPermissions?.EditMetrics;

  useEffect(() => {
    (async() => {
      const fetchedMetrics = await trainingAPI.getTeamMetrics(undefined, undefined, undefined, undefined, undefined, true);
      const sortedMetrics = [...fetchedMetrics]
        .filter((metric) => !metric.is_user_defined || metric.metric_type === "gps")
        .sort((a, b) => (a.order) - (b.order));
      setMetrics(sortedMetrics);
    })()
  }, [])

  useEffect(() => {
    setItemList(metrics);

    let key_metrics = [], show_key_icon = {};
    metrics?.map((metric, index) => {
      if (metric.is_key_metric === true) {
        key_metrics.push(metric.key); 
        show_key_icon[index] = true;
      } else {
        show_key_icon[index] = false;
      }
    })
    setKeyMetricSelection(key_metrics);
    setShowKeyIcon({...showKeyIcon, ...show_key_icon});
    
    // Fetch color strategies
    dispatch(getColorStrategies());
  }, [metrics, dispatch]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDrop = ({ active, over }) => {
    if (!hasEditMetricsPermission || !over || active.id === over.id) return;
    const oldIndex = itemList.findIndex((item) => `${item.key}` === active.id);
    const newIndex = itemList.findIndex((item) => `${item.key}` === over.id);
    setItemList(arrayMove(itemList, oldIndex, newIndex));
  };

  const handleSave = () => {
    setIsModalVisible(false);

    trainingAPI
      .saveMetricOrdering(
        itemList.reduce(
          (cur, item, index) => ({ 
            ...cur, 
            // UDM ids and native metric_ids live in separate tables and can collide;
            // key UDM entries by their unique `c<id>` key to avoid clobbering.
            [item.is_user_defined ? item.key : item.metric_id]: {
              index: index + 1,
              decimal_places: item.decimal_places,
              weekly_bucket_multiple: item.weekly_bucket_multiple,
              is_key_metric: keyMetricSelection.includes(item.key),
              visible: item.visible,
              color_strategy_id: item.color_strategy_id,
              is_user_defined: !!item.is_user_defined
            },
          }),
          {}
        )
      )
      .then((data) => {
        message.success("Metrics updated successfully");
        getAllMetrics();
      })
      .catch((err) => message.error("Error saving metric order"));
  };

  const handleMetricClick = (metric_key, metric_index, checked) => {
    // Check permission before allowing key metric selection
    if (!hasEditMetricsPermission) {
      return;
    }
    
    setShowKeyIcon({...showKeyIcon, [metric_index]: checked});
    if (checked) {
      setKeyMetricSelection([...keyMetricSelection, metric_key]);
      setItemList(itemList?.map((metric) => {
        if (metric.key === metric_key) {
          return {...metric, visible: true, is_key_metric: true}
        }
        return metric
      }))
    } else {
      setKeyMetricSelection(keyMetricSelection.filter(key => key !== metric_key));
      setItemList(itemList?.map((metric) => {
        if (metric.key === metric_key) {
          return {...metric, is_key_metric: false}
        }
        return metric
      }))
    }
  }

  const handleVisibleClick = (item, currVisibility) => {
    // Check permission before allowing visibility changes
    if (!hasEditMetricsPermission) {
      return;
    }
    
    const changeVisibilityTo = !currVisibility

    const metricIndex = itemList.findIndex((e) => e.key === item.key)
    const metricKeyRealTime = showKeyIcon[metricIndex]

    if (metricKeyRealTime && changeVisibilityTo === false) {
      message.warning("Key metrics cannot be hidden");
      return
    }


    setItemList(itemList?.map((metric) => {
      if (metric.key === item.key) {
        return {...metric, visible: changeVisibilityTo};
      }
      return metric;
    }))
  }

  const handleDecimalChange = (metric_key, value) => {
    if (!hasEditMetricsPermission) return;
    setItemList(itemList?.map((metric) => {
      if (metric.key === metric_key) return {...metric, decimal_places: value};
      return metric;
    }));
  };

  const handleWeeklyMultiplierChange = (metric_key, value) => {
    if (!hasEditMetricsPermission) return;
    setItemList(itemList?.map((metric) => {
      if (metric.key === metric_key) return {...metric, weekly_bucket_multiple: value};
      return metric;
    }));
  };

  const handleColorStrategyChange = (metric_key, strategy_id) => {
    // Check permission before allowing color strategy changes
    if (!hasEditMetricsPermission) {
      return;
    }

    setItemList(itemList?.map((metric) => {
      if (metric.key === metric_key) {
        return {...metric, color_strategy_id: strategy_id};
      }
      return metric;
    }));
  };

  const showModal = () => {
    if (keyMetricSelection.length !== 4) {
      message.error("Please select relevant 4 key metrics.")
      return
    }

    //Check if key metrics are changed
    let oldKeyMetrics = [];
    itemList?.map((metric) => {
      if (metric.is_key_metric) {
        oldKeyMetrics.push(metric.key);
      }
    })

    if (keyMetricSelection.length !== oldKeyMetrics.length) return false;  
    const keyMetricChanged = keyMetricSelection.every(item => oldKeyMetrics.includes(item));
    if (!keyMetricChanged) {
      setIsModalVisible(true);
    } else {
      handleSave()
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <div className="metrics-settings">
      <div className="metrics-settings-help">
        {METRIC_SETTINGS_HELP.map(([label, text]) => (
          <div key={label}>
            <Text strong>{label}:</Text> <Text type="secondary">{text}</Text>
          </div>
        ))}
      </div>
      <div className="metrics-settings-header">
        <Modal title="Are you sure?" open={isModalVisible} onOk={handleSave} onCancel={handleCancel} width={"75vw"}>
          <div className="confirm-metric-settings-modal">
            <Typography.Title level={5} type="danger">CAUTION! You are about to change your key metrics</Typography.Title>
            <p>
              Changing your key metrics can have unexpected consequences to your analysis and reports.
              Please read the following information to understand how this affects your account.
            </p>
            <ol>
              <li>
                <strong>Intensity</strong>: Your 4 key metrics are used to calculate the score and intensity of a practice on a given day. Changing them will update all the values (historical values included) on the calendar.
              </li>
              <li>
                <strong>ACWR and Readiness Table</strong>: The 4 metrics that appear on the table on the Overview Tab and the readiness derived from them will be updated to use the new key metrics.
              </li>
              <li>
                <strong>GPS Report</strong>: Your key metrics may have some extra analysis in your GPS Reports, either in the form of charts or tiles. Changing the 4 key metrics will be reflected on the report.
              </li>
              <li>
                <strong>Overworked/Underworked Flags</strong>: Your key metrics are used to determine if a player is flagged for being underworked or overworked. Changing your key metrics may affect the players you see being flagged in your GPS Reports.
              </li>
            </ol>
          </div>
        </Modal>
        <PermissionedButton
          type="primary"
          onClick={showModal}
          permissionCheck="EditMetrics"
        >
          Save Changes
        </PermissionedButton>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDrop}
      >
        <SortableContext
          items={itemList.map((item) => `${item.key}`)}
          strategy={verticalListSortingStrategy}
        >
          <div className={custom_colors_enabled ? "metrics-settings-list" : "metrics-settings-list no-colors"}>
            {itemList?.map((item, index) => (
              <SortableMetricItem
                key={item.key}
                item={item}
                index={index}
                keyMetricSelection={keyMetricSelection}
                showKeyIcon={showKeyIcon}
                hasEditMetricsPermission={hasEditMetricsPermission}
                custom_colors_enabled={custom_colors_enabled}
                colorStrategies={colorStrategies}
                onKeyMetricClick={handleMetricClick}
                onVisibleClick={handleVisibleClick}
                onDecimalChange={handleDecimalChange}
                onWeeklyMultiplierChange={handleWeeklyMultiplierChange}
                onColorStrategyChange={handleColorStrategyChange}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};
